import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { swrKeys } from '@shared/api/swr/keys';
import { stableWalletAddressesParams } from '@shared/api/lib/wallet-monitoring-query';
import type { WalletAddressesMonitoringResponse } from '@shared/api/contracts';
import {
  mergePagedLists,
  monitoringPageCount,
  toWalletAddressQuery,
  WALLET_MONITORING_FETCH_LIMIT,
  type WalletFilterCatalog,
  type WalletsMonitoringFilters,
} from '@entities/wallets';
import { useApiServices } from '@shared/lib/use-api-services';

const SEARCH_DEBOUNCE_MS = 300;

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

export const useWalletAddresses = (
  walletId: number | null,
  enabled: boolean,
  filters: WalletsMonitoringFilters,
  catalog?: WalletFilterCatalog,
) => {
  const services = useApiServices();
  const debouncedSearch = useDebouncedValue(filters.search, SEARCH_DEBOUNCE_MS);

  const queryParams = useMemo(
    () =>
      toWalletAddressQuery(
        {
          ...filters,
          search: debouncedSearch,
        },
        1,
        WALLET_MONITORING_FETCH_LIMIT,
        {
          networkCodes: catalog?.networkCodes,
          currencyCodes: catalog?.currencyCodes,
        },
      ),
    [filters, debouncedSearch, catalog],
  );

  const swrKey = useMemo(() => {
    if (!enabled || walletId == null) return null;
    return swrKeys.wallets.addresses(walletId, stableWalletAddressesParams(queryParams));
  }, [enabled, walletId, queryParams]);

  const { data, error, isLoading, mutate } = useSWR<WalletAddressesMonitoringResponse>(
    swrKey,
    async () => {
      if (walletId == null) {
        throw new Error('Wallet addresses are requested only for an open wallet');
      }

      const first = await services.wallets.getWalletAddresses(walletId, queryParams);
      const totalPages = monitoringPageCount(first.pagination?.totalPages);
      if (totalPages === 1) return first;

      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
          services.wallets.getWalletAddresses(walletId, { ...queryParams, page: index + 2 }),
        ),
      );
      return mergePagedLists([first, ...rest]);
    },
  );

  const addresses = useMemo(() => data?.data ?? [], [data?.data]);

  return {
    addresses,
    summary: data?.summary,
    facets: data?.facets,
    isLoading,
    error,
    mutate,
  };
};
